import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaEnvelope } from 'react-icons/fa';
import { jwtDecode } from 'jwt-decode';
import * as PATH from '../../../CentralisedLink';
import {
  EMAIL_MIGRATION_ALREADY_COMPLETED_ERROR,
  requestEmailChange,
} from '../../../Redux/Dashboard/External/EmailMigration/EmailMigrationAction';
import { HELIXOPS_EMAIL_DOMAIN, PENDING_HELIX_EMAIL_KEY } from '../../../Constants/emailMigration';

const getFirstStringValue = (values) => {
  return values.find((value) => typeof value === 'string' && value.trim()) || '';
};

const getUserEmail = (authState) => {
  const currentUser = authState?.currentUser || {};
  const authUser = authState?.user || {};
  let decodedToken = authState?.decodedToken || {};

  if (!decodedToken.email && authState?.accessToken) {
    try {
      decodedToken = jwtDecode(authState.accessToken) || {};
    } catch (error) {
      decodedToken = authState?.decodedToken || {};
    }
  }

  return getFirstStringValue([
    currentUser.email,
    currentUser.username,
    currentUser.emailAddress,
    currentUser.userEmail,
    currentUser.preferred_username,
    currentUser.upn,
    currentUser.sub,
    authUser.email,
    authUser.username,
    authUser.emailAddress,
    decodedToken.email,
    decodedToken.username,
    decodedToken.emailAddress,
    decodedToken.preferred_username,
    decodedToken.upn,
    decodedToken.sub,
  ]);
};

const isValidEmailFormat = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const UpdateEmail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const { requestLoading } = useSelector((state) => state.EmailMigration);
  const currentEmail = getUserEmail(authState);
  const [newEmail, setNewEmail] = useState('');
  const [submitError, setSubmitError] = useState('');
  const normalizedCurrentEmail = currentEmail.trim().toLowerCase();
  const normalizedNewEmail = newEmail.trim().toLowerCase();

  const validationError = useMemo(() => {
    if (!newEmail.trim()) {
      return `Enter your ${HELIXOPS_EMAIL_DOMAIN} email address.`;
    }

    if (!isValidEmailFormat(newEmail.trim())) {
      return 'Enter a valid email address.';
    }

    if (!normalizedNewEmail.endsWith(HELIXOPS_EMAIL_DOMAIN)) {
      return `Email address must end with ${HELIXOPS_EMAIL_DOMAIN}.`;
    }

    if (normalizedCurrentEmail && normalizedNewEmail === normalizedCurrentEmail) {
      return 'New email address must be different from your current email.';
    }

    return '';
  }, [newEmail, normalizedCurrentEmail, normalizedNewEmail]);

  const isEmailValid = Boolean(newEmail.trim()) && !validationError;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    if (!isEmailValid) {
      return;
    }

    const trimmedNewEmail = newEmail.trim();
    const result = await dispatch(requestEmailChange(trimmedNewEmail));

    if (result.success) {
      sessionStorage.setItem(PENDING_HELIX_EMAIL_KEY, trimmedNewEmail);
      navigate(PATH.EMAIL_VERIFICATION_SENT_PATH);
      return;
    }

    if (result.error === EMAIL_MIGRATION_ALREADY_COMPLETED_ERROR) {
      sessionStorage.removeItem(PENDING_HELIX_EMAIL_KEY);
      navigate(PATH.EMAIL_UPDATED_PATH, { state: { alreadyCompleted: true } });
      return;
    }

    setSubmitError(result.error || 'Something went wrong. Please try again.');
  };

  return (
    <main className="update-email-page">
      <section className="update-email-panel" aria-labelledby="update-email-title">
        <div className="update-email-hero-icon" aria-hidden="true">
          <FaEnvelope />
        </div>
        <h1 id="update-email-title" className="update-email-title">
          Update Your Email Address
        </h1>
        <p className="update-email-subtitle">
          Update your account to use your {HELIXOPS_EMAIL_DOMAIN} email address
        </p>

        <form className="update-email-card" onSubmit={handleSubmit} noValidate>
          <label className="update-email-field">
            <span className="update-email-label">Current Email Address</span>
            <input
              type="email"
              className="update-email-input update-email-input--readonly"
              value={currentEmail || 'Current email not available'}
              readOnly
            />
          </label>

          <label className="update-email-field">
            <span className="update-email-label">New Email Address</span>
            <input
              type="email"
              className={`update-email-input ${newEmail && validationError ? 'update-email-input--error' : ''}`}
              value={newEmail}
              onChange={(event) => {
                setNewEmail(event.target.value);
                setSubmitError('');
              }}
              placeholder={`name${HELIXOPS_EMAIL_DOMAIN}`}
              autoComplete="email"
            />
            {newEmail && validationError && (
              <span className="update-email-error">{validationError}</span>
            )}
            {submitError && (
              <span className="update-email-error">{submitError}</span>
            )}
          </label>

          <div className="update-email-info">
            We'll send a verification link to your new email address. Your account will be updated after verification.
          </div>

          <button
            type="submit"
            className="update-email-submit"
            disabled={!isEmailValid || requestLoading}
          >
            {requestLoading ? 'Sending...' : 'Send Verification Link'}
          </button>
        </form>
      </section>
    </main>
  );
};

export default UpdateEmail;
